import React from "react";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import DeleteIcon from "@material-ui/icons/Delete";
import { generalFilter } from "../../../../util/filter";
import { useStressEntryContext } from "./StressEntryContext";
import {
  STRESSOR_CHECK_IN_MODE,
  STRESSOR_NOT_APPLY_METADATA,
} from "./constants";
import { DateTimePicker } from "@material-ui/pickers";

export const useUnitCheckerColumns = (UnitVersion?: any) => {
  const { state, dispatch } = useStressEntryContext();

  const ID = {
    Header: "Id",
    accessor: "id",
    filter: generalFilter,
  };

  const SERIAL_NUMBER = {
    Header: "Serial Number",
    accessor: "serial_number",
    filter: generalFilter,
  };

  const MODEL = {
    Header: "Model",
    accessor: "model",
    filter: generalFilter,
  };
  const PROCEDURE_TYPE = {
    Header: "Procedure Type",
    accessor: "procedureType",
    filter: generalFilter,
  };
  const NAME = {
    Header: "Name",
    accessor: "name",
    id: "name",
    Cell: (tableProps: any) => {
      const procedureResult = state.checkedUnits.get(
        tableProps.row.values.serial_number,
      );

      const idx = tableProps.row.original.metadataIndex ?? 0;
      const metadataItem =
        procedureResult?.siblings?.[idx] ?? procedureResult?.sibling;

      return (
        <Typography variant="body1">{metadataItem?.name || "N/A"}</Typography>
      );
    },
  };

  const METADATA = {
    Header: "Metadata",
    accessor: "metadata",
    id: "metadata",
    Cell: (tableProps: any) => {
      const failureoptions = [
        "Laminate pulled out",
        "Broken front glass",
        "Broken rear glass",
        "Broken frame",
        "Slipped out of clamps",
        "Undetermined/Multiple",
      ];
      const [dateMetadata, setDateMetadata] = React.useState<any>(null);
      const [tcMetadata, setTcMetadata] = React.useState<string>(""); // ✅ moved outside if
      const [tcMetadataMap, setTcMetadataMap] = React.useState<
        Record<number, string>
      >({});
      const [UVMetadata, setUVMetadata] = React.useState<string>("");

      const serialNumber = tableProps.row.values.serial_number;
      const procedureResult = state.checkedUnits.get(
        tableProps.row.values.serial_number,
      );

      const idx = tableProps.row.original.metadataIndex ?? 0;
      const metadataItem =
        procedureResult?.siblings?.[idx] ?? procedureResult?.sibling;

      const isV12SmlTtf =
        UnitVersion?.version === "v12" &&
        state.procedureResultIds?.some(
          (p: any) => p.procedureDefinitionName === "SML-TTF",
        );

      const showFailureModeDropdown =
        state.isSMLTestCheck &&
        isV12SmlTtf &&
        metadataItem?.name === "Failure Mode";

      if (metadataItem === STRESSOR_NOT_APPLY_METADATA)
        return <Typography variant="body1">N/A</Typography>;

      // React.useEffect(() => {
      //   if (state.isThermalCyclingCheck && UnitVersion?.apply_current !== undefined) {
      //     const value = String(UnitVersion.apply_current);
      //     setTcMetadata(value);
      //     dispatch({
      //       type: "SET_UPDATED_METADATA",
      //       payload: {
      //         metadata: value,
      //         serialNumber: tableProps.row.values.serial_number,
      //       },
      //     });
      //   }
      // }, [UnitVersion?.apply_current, state.isThermalCyclingCheck, dispatch]);

      const matchedResult = React.useMemo(() => {
        if (UnitVersion?.results) {
          return UnitVersion.results[tableProps.row.index];
        }
        return null;
      }, [UnitVersion, tableProps.row.index]);

      const matchedApplyCurrent =
        matchedResult?.apply_current != null
          ? String(matchedResult.apply_current)
          : "";

      const matchedMessage =
        matchedResult?.apply_current == null ? matchedResult?.message : "";

      React.useEffect(() => {
        if (state.isThermalCyclingCheck && matchedApplyCurrent !== "") {
          setTcMetadata(matchedApplyCurrent);
          dispatch({
            type: "SET_UPDATED_METADATA",
            payload: {
              metadata: matchedApplyCurrent,
              serialNumber,
            },
          });
        }
      }, [matchedApplyCurrent, state.isThermalCyclingCheck, serialNumber]);

      React.useEffect(() => {
        if (state.isUvidTestCheck && matchedApplyCurrent !== "") {
          setUVMetadata(matchedApplyCurrent);

          dispatch({
            type: "SET_UPDATED_METADATA",
            payload: {
              metadata: matchedApplyCurrent,
              serialNumber,
            },
          });
        }
      }, [matchedApplyCurrent, state.isUvidTestCheck, serialNumber]);

      if (
        state.isThermalCyclingCheck &&
        state.mode === STRESSOR_CHECK_IN_MODE &&
        UnitVersion?.procedure_group === "TC"
      ) {
        return (
          <div>
            <TextField
              type="number"
              value={tcMetadata}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                setTcMetadata(value);
                dispatch({
                  type: "SET_UPDATED_METADATA",
                  payload: {
                    metadata: value,
                    serialNumber,
                  },
                });
              }}
            />
            {matchedApplyCurrent === "" && matchedMessage && (
              <Typography
                variant="caption"
                style={{ color: "red", marginTop: 4, display: "block" }}
              >
                {matchedMessage}
              </Typography>
            )}
          </div>
        );
      }

      if (
        state.isUvidTestCheck &&
        state.mode === STRESSOR_CHECK_IN_MODE &&
        UnitVersion?.procedure_group === "UV"
      ) {
        return (
          <div>
            <TextField
              type="number"
              value={UVMetadata}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                setUVMetadata(value);

                dispatch({
                  type: "SET_UPDATED_METADATA",
                  payload: {
                    metadata: value,
                    serialNumber,
                  },
                });
              }}
            />

            {matchedApplyCurrent === "" && matchedMessage && (
              <Typography
                variant="caption"
                style={{ color: "red", marginTop: 4, display: "block" }}
              >
                {matchedMessage}
              </Typography>
            )}
          </div>
        );
      }

      if (showFailureModeDropdown) {
        return (
          <TextField
            select
            fullWidth
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              dispatch({
                type: "SET_SMLFAIL",
                payload: {
                  serialNumber,
                  FailureType: e.target.value,
                },
              });
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    borderRadius: 0,
                  },
                },
              },
            }}
          >
            <MenuItem value="">
              <em>Select failure mode</em>
            </MenuItem>

            {failureoptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        );
      }

      // if (procedureResult?.sibling === STRESSOR_NOT_APPLY_METADATA)
      //   return <Typography variant="body1">N/A</Typography>;

      // const measurementResultTypeField =
      //   procedureResult?.sibling?.measurement_results[0]
      //     ?.measurement_result_type_field;

      const measurementResultTypeField =
        metadataItem?.measurement_results[0]?.measurement_result_type_field;

      if (measurementResultTypeField) {
        switch (measurementResultTypeField) {
          case "result_double":
            return (
              <TextField
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch({
                    type: "SET_UPDATED_METADATA",
                    payload: {
                      metadata: e.currentTarget.value,
                      serialNumber: tableProps.row.values.serial_number,
                    },
                  });
                }}
              />
            );
          case "result_string":
            return (
              <TextField
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch({
                    type: "SET_UPDATED_METADATA",
                    payload: {
                      metadata: e.currentTarget.value,
                      serialNumber: tableProps.row.values.serial_number,
                    },
                  });
                }}
              />
            );
          case "result_datetime":
            return (
              <DateTimePicker
                label={"Select a date"}
                value={dateMetadata}
                inputVariant="outlined"
                format="YYYY-MM-DD HH:mm:ss"
                onChange={(date: any) => {
                  setDateMetadata(moment(date).format("YYYY-MM-DD HH:mm:ss"));
                  dispatch({
                    type: "SET_UPDATED_METADATA",
                    payload: {
                      metadata: moment(date).format("YYYY-MM-DD HH:mm:ss"),
                      serialNumber: tableProps.row.values.serial_number,
                    },
                  });
                }}
              />
            );
        }
      }

      return <Typography variant="body1">N/A</Typography>;
    },
  };

  const LOW_CURRENT = {
    Header: "Applied Current(low)",
    accessor: "low_current",
    id: "low_current",
    Cell: ({ row }: any) => {
      const serialNumber = row.values.serial_number;

      const { state } = useStressEntryContext();

      const entry = state.checkedUnits?.get?.(serialNumber) ?? {};
      const metadata = entry?.metadata;

      if (state.isThermalCyclingCheck) {
        const [displayValue, setDisplayValue] = React.useState("");

        React.useEffect(() => {
          if (!isNaN(Number(metadata))) {
            const calculated = (Number(metadata) * 0.1).toFixed(2);
            setDisplayValue(calculated);

            dispatch({
              type: "SET_UPDATED_LOW_CURRENT",
              payload: {
                lowCurrent: calculated,
                serialNumber,
              },
            });
          }
        }, [metadata, serialNumber, dispatch]);

        return <TextField value={displayValue} disabled></TextField>;
      }
      if (state.isHumidityTestCheck) {
        const [humidityValue, setHumidityValue] = React.useState(0.1);
        return (
          <TextField
            value={humidityValue}
            onChange={(e) => {
              const newValue = Number(e.target.value);
              console.log("Humidity Value Changed:", newValue);
              setHumidityValue(newValue);
              dispatch({
                type: "SET_APPLIED_CURRENT_HF",
                payload: {
                  applied_current: newValue,
                },
              });
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">A</InputAdornment>,
            }}
          />
        );
      }
      return <Typography variant="body1">N/A</Typography>;
    },
  };

  const APPLY_CURRENT = {
    Header: "Apply Current",
    accessor: "Apply_current",
    id: "Apply_current",
    Cell: ({ row }: any) => {
      if (state.isCidTestCheck) {
        const [cidValue, setcidValue] = React.useState<number | null>(null);
        return (
          <TextField
            type="number"
            value={cidValue}
            inputProps={{ step: 0.1 }}
            onChange={(e) => {
              const input = Number(e.target.value);
              const newValue =
                input === 0 ? 0 : parseFloat(Number(input).toFixed(1));
              setcidValue(newValue);

              dispatch({
                type: "SET_APPLIED_CURRENT_CID",
                payload: {
                  applied_current: newValue,
                },
              });
            }}
          />
        );
      }

      return <Typography variant="body1">N/A</Typography>;
    },
  };
  const DELETE = {
    Header: "",
    id: "delete",
    accessor: "delete",

    Cell: (tableProps: any) => (
      <IconButton
        onClick={() => {
          const dataCopy: any[] = [...state.checkedUnitsTable];
          dataCopy.splice(tableProps.row.index, 1);
          dispatch({
            type: "SET_UPDATED_CHECKED_UNITS_TABLE",
            payload: {
              updatedCheckedUnitsTable: dataCopy,
              serialNumber: tableProps.row.values.serial_number,
            },
          });
        }}
      >
        <DeleteIcon />
      </IconButton>
    ),
  };

  return {
    ID,
    SERIAL_NUMBER,
    MODEL,
    PROCEDURE_TYPE,
    NAME,
    METADATA,
    DELETE,
    LOW_CURRENT,
    APPLY_CURRENT,
  };
};
